import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MsalAuthProvider, useMsalAuth } from './context/MsalAuthContext';
import { msalConfig, isMsalConfigured } from './services/msalConfig';
import CalendarPage from './pages/CalendarPage';
import M365CalendarPage from './pages/M365CalendarPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import CompanyAdminPage from './pages/CompanyAdminPage';
import SetupPasswordPage from './pages/SetupPasswordPage';
import NotificationBell from './components/common/NotificationBell';
import './App.css';
import './styles/animations.css';

// MSALインスタンスを作成（設定がある場合のみ）
const msalInstance = isMsalConfigured()
  ? new PublicClientApplication(msalConfig)
  : null;

/**
 * M365モード用ナビゲーション
 * Azure ADでログインしたユーザー向け
 */
function M365Navigation() {
  const { isAuthenticated, user, logout, loading } = useMsalAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleMenuKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenu();
    } else if (e.key === 'Escape') {
      setIsMenuOpen(false);
    }
  };

  return (
    <nav className="navigation">
      <div className="nav-brand">
        <Link to="/">会議室カレンダー</Link>
      </div>
      <div className="nav-links">
        {isAuthenticated ? (
          <>
            <NotificationBell />
            <div className="user-menu" ref={menuRef}>
              <div
                className="user-icon"
                onClick={toggleMenu}
                onKeyDown={handleMenuKeyDown}
                role="button"
                tabIndex={0}
                aria-expanded={isMenuOpen}
                aria-haspopup="true"
                aria-label={`ユーザーメニュー: ${user?.name || user?.email}`}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <span className="user-name">{user?.name || user?.email}</span>
              {isMenuOpen && (
                <div className="user-dropdown" role="menu">
                  <button onClick={() => { logout(); setIsMenuOpen(false); }} className="dropdown-item" role="menuitem">
                    ログアウト
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <Link to="/login" className="nav-link">
            {loading ? '読み込み中...' : 'ログイン'}
          </Link>
        )}
      </div>
    </nav>
  );
}

/**
 * 既存モード用ナビゲーション（内部認証）
 */
function Navigation() {
  const location = useLocation();
  const { isAuthenticated, user, logout, isSystemAdmin, isCompanyAdmin } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  // メニュー開閉のトグル
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // キーボード操作
  const handleMenuKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenu();
    } else if (e.key === 'Escape') {
      setIsMenuOpen(false);
    }
  };

  // ドロップダウン内のキーボードナビゲーション
  const handleDropdownKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsMenuOpen(false);
    }
  };

  return (
    <nav className="navigation">
      <div className="nav-brand">
        <Link to="/">会議室カレンダー</Link>
      </div>
      <div className="nav-links">
        {isAuthenticated && (
          <>
            {isCompanyAdmin() && (
              <Link
                to="/company-admin"
                className={location.pathname === '/company-admin' ? 'active' : ''}
              >
                管理
              </Link>
            )}
            {isSystemAdmin() && (
              <Link
                to="/admin"
                className={location.pathname === '/admin' ? 'active' : ''}
              >
                管理
              </Link>
            )}
          </>
        )}

        {isAuthenticated ? (
          <div className="user-menu" ref={menuRef}>
            <div
              className="user-icon"
              onClick={toggleMenu}
              onKeyDown={handleMenuKeyDown}
              role="button"
              tabIndex={0}
              aria-expanded={isMenuOpen}
              aria-haspopup="true"
              aria-label={`ユーザーメニュー: ${user?.display_name || user?.name || user?.email}`}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </div>
            <span className="user-name">{user?.display_name || user?.name || user?.email}</span>
            {isMenuOpen && (
              <div className="user-dropdown" role="menu" onKeyDown={handleDropdownKeyDown}>
                <Link to="/profile" className="dropdown-item" role="menuitem" onClick={() => setIsMenuOpen(false)}>
                  プロフィール編集
                </Link>
                <button onClick={() => { logout(); setIsMenuOpen(false); }} className="dropdown-item" role="menuitem">
                  ログアウト
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link
            to="/login"
            className={location.pathname === '/login' ? 'active' : ''}
          >
            ログイン
          </Link>
        )}
      </div>
    </nav>
  );
}

/**
 * M365モード用ログインページ
 * Microsoftアカウントでログイン
 */
function M365Login() {
  const { login, isAuthenticated, loading, error } = useMsalAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page">
      <div className="login-container">
        <div className="login-form">
          <h2>ログイン</h2>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            Microsoftアカウントでログインしてください
          </p>

          {error && <div className="error-message">{error}</div>}

          <button
            onClick={login}
            className="login-button microsoft-login"
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              backgroundColor: '#0078d4',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 21 21" fill="white">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            {loading ? 'ログイン中...' : 'Microsoftでログイン'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 既存モード用ログインページ（内部認証）
 */
function Login() {
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 既にログイン済みの場合はホーム（カレンダー）にリダイレクト
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);

    if (result.success) {
      // ログイン成功時はホーム（カレンダー）にリダイレクト
      window.location.href = '/';
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="login-container">
        <div className="login-form">
          <h2>ログイン</h2>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">メールアドレス</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@tplg.co.jp"
                required
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">パスワード</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="admin123"
                required
                disabled={loading}
              />
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'ログイン中...' : 'ログイン'}
            </button>
          </form>

          <div className="login-info">
            <p><strong>テスト用アカウント:</strong></p>
            <p>システム管理者: admin@tplg.co.jp / admin123</p>
            <p>企業管理者: company_admin@tplg.co.jp / companyuser123</p>
            <p>一般ユーザー: user1@tplg.co.jp / user123</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * M365モード用アプリ
 * Microsoft Graph APIでカレンダーを閲覧
 */
function M365App() {
  return (
    <MsalAuthProvider>
      <Router>
        <div className="App">
          <M365Navigation />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<M365CalendarPage />} />
              <Route path="/login" element={<M365Login />} />
            </Routes>
          </main>
        </div>
      </Router>
    </MsalAuthProvider>
  );
}

/**
 * 既存モード用アプリ
 * 内部認証と予約機能
 */
function LegacyApp() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Navigation />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<CalendarPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/setup-password" element={<SetupPasswordPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/company-admin" element={<CompanyAdminPage />} />
            </Routes>
          </main>
        </div>
      </Router>
    </AuthProvider>
  );
}

/**
 * メインAppコンポーネント
 * MSAL設定がある場合はM365モード、なければ既存モード
 */
function App() {
  // MSAL設定がある場合はM365モード
  if (msalInstance) {
    return (
      <MsalProvider instance={msalInstance}>
        <M365App />
      </MsalProvider>
    );
  }

  // 既存モード（内部認証）
  return <LegacyApp />;
}

export default App;
